import type { AppSettings, AnalysisResult } from '@/types'

const STORAGE_KEYS = {
  SETTINGS: 'imki-settings',
  GLOBAL_SETTINGS: 'imki-global-settings', // Shared across all accounts
  ANALYSES: 'imki-analyses', // Now also shared across all accounts
  SETTINGS_DB: 'app-settings' // Key for IndexedDB
} as const

const DEFAULT_SETTINGS: AppSettings = {
  mockMode: false,
  geminiApiKey: '', // No longer needed - API key is handled server-side
  geminiModel: 'gemini-2.5-pro', // Correct model
  geminiVisionPrompt: `You are an expert construction site inspector analyzing this image for defects and safety issues.

IMPORTANT: Only report defects that are ACTUALLY VISIBLE in the image. If the construction site looks good with no visible issues, return an empty defects array. Do not force or invent defects that don't exist.

Analyze the image systematically:
1. First, identify the type of construction work visible (electrical, plumbing, HVAC, drywall, etc.)
2. Identify which trades (Gewerke) are present in the image:
   - **Heizung** (Heating): Heating pipes, radiators, boilers, heating systems
   - **Lüftung** (Ventilation): Air ducts, ventilation grilles, fans, air handling units
   - **Sanitär** (Plumbing): Water pipes, drainage, sanitary fixtures, valves
   - **Trockenbau** (Drywall): Gypsum boards, metal studs, drywall installations
   - **Elektro** (Electrical): Cables, junction boxes, switches, electrical panels
3. Check for industry-specific standards and regulations
4. Look for safety hazards and quality issues
5. Assess the general cleanliness and organization of the work area

Focus on these defect types (ONLY if actually present):
{{DEFECT_TYPES}}

SPECIAL ATTENTION for scaffolding defects (MUST use these specific defect_type values):
- Use "scaffolding_distance" when: Gap/distance between scaffolding platform and facade exceeds 30cm (violates DIN 4420/EN 12811, DGUV Information 201-011)
- Use "scaffolding_lift_placement" when: Construction hoist/lift/Schrägaufzug is improperly mounted on scaffolding deck (safety violation)

IMPORTANT: When you detect scaffolding distance issues, you MUST set defect_type to "scaffolding_distance", NOT "other" or "safety_hazard"!
IMPORTANT: When you detect lift placement issues, you MUST set defect_type to "scaffolding_lift_placement", NOT "other" or "safety_hazard"!

For each defect found:
- Identify the exact location in the image
- Assess the severity based on safety risk and regulatory compliance
- Provide specific German trade terminology
- Identify which trades are affected by this defect
- Consider the impact on subsequent work phases

Severity Guidelines:
- HIGH: Immediate safety hazard, regulatory violation, or structural integrity issue
- MEDIUM: Quality issue that needs correction before next phase, potential for damage
- LOW: Minor issue, cosmetic defect, or general housekeeping matter

Maximum defects to detect: {{MAX_DEFECTS}}

RESPOND WITH VALID JSON ONLY, using this exact structure:
{
  "defects": [
    {
      "defect_type": "missing_insulation|exposed_wire|missing_grounding|missing_labeling|debris|missing_edge_protection|safety_hazard|scaffolding_distance|scaffolding_lift_placement|other",
      "description": "Detailed description in German using proper trade terminology",
      "bounding_box": {
        "x_min": 0.0,  // Left edge (0 = far left, 1 = far right)
        "y_min": 0.0,  // Top edge (0 = top, 1 = bottom)
        "x_max": 1.0,  // Right edge
        "y_max": 1.0   // Bottom edge
      },
      "confidence": 0.0,  // Your confidence in this detection (0.0 to 1.0)
      "severity": "low|medium|high",
      "trades": ["Heizung", "Sanitär"]  // Affected trades for this specific defect
    }
  ],
  "detected_trades": ["Heizung", "Lüftung", "Sanitär", "Trockenbau", "Elektro"],  // All trades visible in the image
  "overall_assessment": "Comprehensive assessment in German describing the overall condition, main issues found, and recommendations for remediation",
  "total_defects": 0
}

Remember:
- Bounding boxes must be precise and cover only the defect area
- Coordinates are normalized (0-1 range)
- Use professional German construction terminology
- Be specific about locations and materials
- Always include detected_trades array with all visible trades
- For each defect, specify which trades are affected
- If no defects are visible, return: {"defects": [], "detected_trades": [...], "overall_assessment": "Die Baustelle zeigt keine erkennbaren Mängel. Alle sichtbaren Arbeiten entsprechen den Standards.", "total_defects": 0}`,
  geminiEnabledDefects: {
    missing_insulation: true,
    missing_labeling: true,
    missing_grounding: true,
    missing_edge_protection: true,
    exposed_wire: true,
    debris: true,
    safety_hazard: true,
    scaffolding_distance: true,
    scaffolding_lift_placement: true,
    other: true
  },
  geminiMaxDefects: 10,
  // New feature settings
  duplicateDetection: true, // Enable by default
  duplicateThreshold: 0.95, // 95% similarity threshold
  enabledTrades: {
    heizung: true,
    lueftung: true,
    sanitaer: true,
    trockenbau: true,
    elektro: true
  },
  pushNotifications: {
    enabled: false, // User needs to enable
    onQueueAdd: true,
    onProcessComplete: true,
    onError: true
  },
  offlineMode: {
    autoSync: true,
    maxQueueSize: 50,
    retryAttempts: 3
  },
  solutionSettings: {
    showSolutionButton: true,
    autoOpenSolutions: false,
    customSolutionsEnabled: false,
    useAISolutions: false,  // Default to predefined solutions
    aiSolutionPrompt: `Du bist ein zertifizierter Bauleiter mit 20 Jahren Erfahrung und Spezialisierung auf Qualitätssicherung nach deutschen Bauvorschriften. Analysiere den vorliegenden Mangel und erstelle eine professionelle Handlungsempfehlung.

=== MANGEL-ANALYSE ===
Defekttyp: {{DEFECT_TYPE}}
Beschreibung: {{DEFECT_DESCRIPTION}}
{{TRADE_INFO}}
{{IMAGE_CONTEXT}}

=== DEINE AUFGABE ===
Erstelle eine SPEZIFISCHE, UMSETZBARE Lösung unter Berücksichtigung von:

1. SICHERHEIT & VORSCHRIFTEN
   - Relevante DIN-Normen, VDE-Richtlinien, EnEV-Vorgaben
   - Arbeitsschutzbestimmungen (DGUV)
   - Brandschutzanforderungen wenn relevant

2. TECHNISCHE ANALYSE
   - Ursachenanalyse: Warum ist dieser Mangel entstanden?
   - Folgeschäden: Was passiert wenn nicht behoben?
   - Abhängigkeiten: Welche anderen Gewerke sind betroffen?

3. PRAKTISCHE UMSETZUNG
   - Verfügbarkeit von Material auf deutschen Baustellen
   - Realistische Zeitschätzung inkl. Trocknungszeiten
   - Koordination mit anderen Gewerken
   - Witterungsbedingungen wenn relevant

4. WIRTSCHAFTLICHKEIT
   - Materialkosten nach aktuellem Marktpreis
   - Arbeitskosten nach Tarifvertrag Bau
   - Preis-Leistungs-Alternativen

5. NACHHALTIGKEIT
   - Langfristige Lösung vs. Provisorium
   - Präventionsmaßnahmen gegen Wiederholung
   - Wartungsintervalle

=== ANTWORT-FORMAT ===
Antworte mit validem JSON. WICHTIG: Alle Angaben müssen konkret und spezifisch für DIESEN Mangel sein!

{
  "title": "Kurzer, präziser Titel (max. 50 Zeichen)",
  "description": "Detaillierte Problembeschreibung mit Auswirkungen auf Bausubstanz, Sicherheit und Nutzung (2-3 Sätze)",
  "steps": [
    "1. Vorbereitung: Konkrete erste Schritte (z.B. Absperrung, Freimachen)",
    "2. Materialvorbereitung: Genau welches Material, welche Menge",
    "3. Hauptarbeit: Detaillierte Arbeitsschritte",
    "4. Qualitätskontrolle: Prüfschritte und Messwerte",
    "5. Dokumentation: Fotos, Protokolle, Abnahme"
  ],
  "estimatedCost": {
    "min": 100,
    "max": 500,
    "unit": "€"
  },
  "estimatedTime": "Realistische Zeitangabe inkl. Trocknungszeiten (z.B. '4-6 Stunden + 24h Trocknungszeit')",
  "materials": [
    "Genaue Materialbezeichnung mit Menge (z.B. 'Mineralwolle WLG 035, 120mm, 5m²')",
    "Befestigungsmaterial (z.B. 'Tellerdübel 10x120mm, 25 Stück')"
  ],
  "tools": [
    "Spezifisches Werkzeug (z.B. 'Dämmstoffmesser 280mm')",
    "Messgeräte wenn nötig (z.B. 'Feuchtigkeitsmessgerät')"
  ],
  "normen": [
    "Konkrete Norm mit Paragraph (z.B. 'DIN 4108-10:2021-03 Abschnitt 5.2')",
    "VDE 0100-420:2019-10 wenn Elektro betroffen"
  ],
  "preventionTips": [
    "Konkrete Präventionsmaßnahme für DIESEN Mangel",
    "Wartungsintervall mit Zeitangabe (z.B. 'Jährliche Sichtprüfung im Frühjahr')"
  ],
  "severity": "low|medium|high|critical"
}

Bewerte Severity nach:
- critical: Gefahr für Leib und Leben, sofortige Sperrung nötig
- high: Bauschaden droht, Behebung innerhalb 48h
- medium: Funktionsbeeinträchtigung, Behebung innerhalb 2 Wochen
- low: Optischer Mangel, bei nächster Wartung beheben`
  },
  customDefects: {
    'missing_insulation': 'Fehlende Isolierung an Rohrleitungen oder Kanälen',
    'exposed_wire': 'Freiliegende elektrische Leitungen ohne Schutz',
    'missing_grounding': 'Fehlende Erdung an metallischen Teilen',
    'missing_labeling': 'Fehlende Beschriftung an Leitungen oder Komponenten',
    'missing_edge_protection': 'Fehlender Kantenschutz an Absturzkanten',
    'debris': 'Bauschutt oder Verschmutzung',
    'safety_hazard': 'Allgemeine Sicherheitsrisiken',
    'scaffolding_distance': 'Gerüstabstand zur Fassade überschreitet 30cm (DIN 4420/EN 12811)',
    'scaffolding_lift_placement': 'Unzulässige Positionierung von Schrägaufzug auf Gerüstlage',
    'other': 'Mangel erkannt'
  },
  systemPrompts: {
    defectDetection: 'Analyze this construction site image. Identify defects: missing insulation on pipes, wrong spacing between installations, contamination or debris, improper installation. List each defect found.',
    imageDescription: 'Describe this construction site image in detail. What building work is visible? What materials, tools, pipes, cables can you see? What is the condition of the construction site? Provide a detailed description.',
    groundingDetection: 'Analyze this construction site image and locate all visible defects with precise coordinates:\n\n🏗️ FIND AND MARK:\n1. CONTAMINATION/DEBRIS: Locate construction debris, gravel, scattered materials, dirt on floor areas\n2. MISSING INSULATION: Find exposed pipes, unprotected wires, bare installations without proper covering\n3. SAFETY HAZARDS: Identify exposed electrical wiring, loose components, tripping hazards\n4. SPACING ISSUES: Mark installations that are too close together or have inadequate clearances\n\nFor each defect found, provide:\n- Exact location with <box>x1,y1,x2,y2</box> coordinates\n- Clear defect description\n- Severity assessment\n\nRespond with detailed coordinates and descriptions for each defect discovered.',
    tradeRecognition: 'Erkenne und benenne alle sichtbaren Gewerke im Bild. Fokussiere dich auf Heizung, Lüftung, Sanitär und Trockenbau.',
    chatResponse: 'Du bist ein hilfreicher Assistent für Bauleiter. Beantworte Fragen im Kontext des gezeigten Bildes mit praktischen, umsetzbaren Ratschlägen.',
    // Gewerke-spezifische Prompts
    heizung: 'Heizung: Analysiere Heizkörper, Heizungsrohre, Wärmepumpen, Thermostate. Achte auf: korrekte Rohrverlegung, Isolierung, Abstände zur Wand (min. 10cm), ordnungsgemäße Befestigung, keine Leckagen.',
    lueftung: 'Lüftung: Analysiere Lüftungskanäle, Ventilatoren, Luftauslässe. Achte auf: korrekte Kanalführung, dichte Verbindungen, ausreichende Befestigung, keine Verschmutzung der Kanäle.',
    sanitaer: 'Sanitär: Analysiere Wasserleitungen, Abwasserleitungen, Armaturen. Achte auf: korrekte Rohrverlegung, Dichtheit, Gefälle bei Abwasser, Isolierung gegen Kondensation.',
    trockenbau: 'Trockenbau: Analysiere Gipskartonwände, Dämmung, Ständerwerk. Achte auf: korrekte Dämmung, gerade Wandführung, ordnungsgemäße Verschraubung, keine Risse.',
    elektro: 'Elektro: Analysiere Kabel, Leitungen, Schalter, Steckdosen. Achte auf: ordnungsgemäße Verlegung, Schutz vor Beschädigung, korrekte Befestigung, Sicherheitsabstände.'
  },
  thresholds: {
    confidenceThreshold: 0.7,
    maxDefectsToShow: 10
  }
}

export class StorageService {
  // Keep synchronous method for backward compatibility
  static getSettings(): AppSettings {
    if (typeof window === 'undefined') return DEFAULT_SETTINGS

    try {
      // Use global settings that are shared across all accounts
      const stored = localStorage.getItem(STORAGE_KEYS.GLOBAL_SETTINGS)
      if (!stored) {
          // Try to migrate from old per-account settings
          const oldSettings = localStorage.getItem(STORAGE_KEYS.SETTINGS)
          if (oldSettings) {
            // Migrate old settings to global
            localStorage.setItem(STORAGE_KEYS.GLOBAL_SETTINGS, oldSettings)
            return JSON.parse(oldSettings)
          }
          return DEFAULT_SETTINGS
      }

      const parsed = JSON.parse(stored)

      if (!parsed.geminiModel || parsed.geminiModel.length < 5) {
        parsed.geminiModel = 'gemini-2.5-pro'
        parsed.mockMode = false
        localStorage.setItem(STORAGE_KEYS.GLOBAL_SETTINGS, JSON.stringify(parsed))
        localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(parsed))
      }
      
      // Deep merge that properly preserves arrays
      const settings: AppSettings = {
        ...DEFAULT_SETTINGS,
        ...parsed,
        // Ensure nested objects are properly merged
        systemPrompts: {
          ...DEFAULT_SETTINGS.systemPrompts,
          ...(parsed.systemPrompts || {})
        },
        thresholds: {
          ...DEFAULT_SETTINGS.thresholds,
          ...(parsed.thresholds || {})
        },
        customDefects: {
          ...DEFAULT_SETTINGS.customDefects,
          ...(parsed.customDefects || {})
        },
        geminiEnabledDefects: {
          ...DEFAULT_SETTINGS.geminiEnabledDefects,
          ...(parsed.geminiEnabledDefects || {})
        },
        solutionSettings: {
          ...DEFAULT_SETTINGS.solutionSettings,
          ...(parsed.solutionSettings || {})
        }
      }
      
      return settings
    } catch (error) {
      console.error('Error loading settings:', error)
      return DEFAULT_SETTINGS
    }
  }

  static saveSettings(settings: AppSettings): void {
    if (typeof window === 'undefined') return

    try {
      // Save to global settings that are shared across all accounts
      localStorage.setItem(STORAGE_KEYS.GLOBAL_SETTINGS, JSON.stringify(settings))
      // Also keep a backup in the old location for compatibility
      localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings))
    } catch (error) {
      console.error('Error saving settings:', error)
    }
  }

  // DEPRECATED: These methods are kept for compatibility but analyses are now in IndexedDB
  static getAnalyses(): AnalysisResult[] {
    // Analyses are now stored in IndexedDB via db.ts
    // This method is deprecated and returns empty array
    console.warn('StorageService.getAnalyses() is deprecated. Use db.ts getAllAnalyses() instead')
    return []
  }

  static saveAnalysis(_analysis: AnalysisResult): void {
    // Analyses are now stored in IndexedDB via db.ts
    // This method is deprecated and does nothing
    console.warn('StorageService.saveAnalysis() is deprecated. Use db.ts saveAnalysis() instead')
  }

  static deleteAnalysis(_id: string): void {
    // Analyses are now stored in IndexedDB via db.ts
    // This method is deprecated and does nothing
    console.warn('StorageService.deleteAnalysis() is deprecated. Use db.ts deleteAnalysis() instead')
  }

  static clearAllData(): void {
    if (typeof window === 'undefined') return

    try {
      // Only clear settings, not analyses (which are in IndexedDB)
      localStorage.removeItem(STORAGE_KEYS.SETTINGS)
      localStorage.removeItem(STORAGE_KEYS.GLOBAL_SETTINGS)
      // Remove deprecated analyses key if it exists
      localStorage.removeItem(STORAGE_KEYS.ANALYSES)
    } catch (error) {
      console.error('Error clearing data:', error)
    }
  }
}